import { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

const defaultUserInfo = {
  user_id: '',
  email: '',
  role: '',
  customer_id: null,
  user_location_ids: [],
  is_single_user: false,
  features: {
    private_lessons: false
  }
};

const normalizeFeatures = (features = {}) => {
  return {
    ...features,
    private_lessons: features?.private_lessons === true
  };
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userInfo, setUserInfo] = useState(defaultUserInfo);
  const [loading, setLoading] = useState(true);

  axios.defaults.withCredentials = true;

  const buildUserFromToken = (token) => {
    const decodedToken = jwtDecode(token);

    return {
      user_id: decodedToken.user_id || '',
      email: decodedToken.email || '',
      role: decodedToken.role || '',
      customer_id: decodedToken.customer_id || null,
      user_location_ids: decodedToken.user_location_ids || [],
      is_single_user: decodedToken.is_single_user || false,
      features: normalizeFeatures(decodedToken.features || {})
    };
  };

  const login = (token) => {
    const decodedUser = buildUserFromToken(token);

    sessionStorage.setItem('jwtToken', token);
    setUserInfo(decodedUser);
    setIsAuthenticated(true);
  };

  const logout = () => {
    sessionStorage.removeItem('jwtToken');
    setIsAuthenticated(false);
    setUserInfo(defaultUserInfo);
  };

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;

    if (userInfo?.role === 'eigenaar') {
      return true;
    }

    return userInfo?.features?.[featureKey] === true;
  };

  useEffect(() => {
    const token = sessionStorage.getItem('jwtToken');

    const publicPaths = [
      '/login',
      '/reset-password',
      '/forgot-password',
      '/forgotpassword'
    ];

    if (!token) {
      setIsAuthenticated(false);
      setUserInfo(defaultUserInfo);
      setLoading(false);

      if (!publicPaths.some((path) => window.location.pathname.startsWith(path))) {
        window.location.href = '/login';
      }

      return;
    }

    try {
      const decodedUser = buildUserFromToken(token);
      setUserInfo(decodedUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Ongeldig token');
      logout();
      setLoading(false);

      if (!publicPaths.some((path) => window.location.pathname.startsWith(path))) {
        window.location.href = '/login';
      }

      return;
    }

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/auth/authenticate`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((response) => {
        if (response.data.Status !== 'Success') {
          logout();

          if (!publicPaths.some((path) => window.location.pathname.startsWith(path))) {
            window.location.href = '/login';
          }
        }
      })
      .catch((error) => {
        console.error('Authenticatie mislukt');
        logout();

        if (!publicPaths.some((path) => window.location.pathname.startsWith(path))) {
          window.location.href = '/login';
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        userInfo,
        setUserInfo,
        loading,
        login,
        logout,
        hasFeature
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);